import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * Writes the shopper's gift message into the cart note as `Gift note: <message>`.
 * The 3PL prints everything after the prefix on the packing slip, so the gift
 * text is always kept at the end of the note and any other note text is preserved.
 *
 * @typedef {Object} CartGiftNoteRefs
 * @property {HTMLTextAreaElement} message - Gift message textarea
 * @property {HTMLInputElement} note - Hidden `note` input submitted with the cart form
 * @property {HTMLElement} count - Remaining character count
 *
 * @extends {Component<CartGiftNoteRefs>}
 */
class CartGiftNote extends Component {
  requiredRefs = ['message', 'note', 'count'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncNote();
  }

  get #prefix() {
    return this.dataset.prefix || 'Gift note:';
  }

  #buildNote() {
    const message = this.refs.message.value.trim();
    const preserved = (this.dataset.preservedNote || '').trim();
    const giftNote = message ? `${this.#prefix} ${message}` : '';

    return [preserved, giftNote].filter(Boolean).join('\n');
  }

  #syncNote() {
    const { message, note, count } = this.refs;
    const remaining = message.maxLength - message.value.length;

    note.value = this.#buildNote();
    count.textContent = String(remaining);
  }

  handleInput = () => {
    this.#syncNote();
    this.#saveNote();
  };

  #saveNote = debounce(async () => {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ note: this.refs.note.value }) }),
        signal: abortController.signal,
      });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to save gift note:', error);
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }, 300);
}

if (!customElements.get('cart-gift-note')) {
  customElements.define('cart-gift-note', CartGiftNote);
}
